import { el, one, type Target } from './dom';
import { attach } from './validation';
import { applyUtcToInputs } from './forms';

function modalElement(modalId: string): HTMLElement | null {
    return one(modalId) as HTMLElement | null;
}

function instance(node: Element) {
    const Modal = window.bootstrap?.Modal;
    return Modal ? Modal.getOrCreateInstance(node) : null;
}

export function showModalElement(node: Element): void {
    const modal = instance(node);
    if (!modal) {
        node.classList.add('show', 'd-block');
        return;
    }
    // Bootstrap ignores show() while the previous hide() is still animating
    // (a save that closes the dialog and immediately reopens it with the next
    // record hits this on a fast server); queue the show behind the hide.
    const transitioning = (modal as unknown as { _isTransitioning?: boolean })._isTransitioning === true;
    if (transitioning && !node.classList.contains('show')) {
        node.addEventListener('hidden.bs.modal', () => modal.show(), { once: true });
        return;
    }
    modal.show();
}

export function hideModalElement(node: Element): void {
    const modal = instance(node);
    if (!modal) {
        node.classList.remove('show', 'd-block');
        return;
    }
    // The mirror of the show-during-hide case above: Bootstrap's hide() returns
    // early while the dialog is still animating open, so a hide issued inside
    // that window is dropped and never fires hidden.bs.modal. The confirm
    // dialog runs its onConfirm from that event, so an OK taken before the
    // dialog settled did nothing at all -- no request, no toast, no dialog.
    const transitioning = (modal as unknown as { _isTransitioning?: boolean })._isTransitioning === true;
    if (transitioning && node.classList.contains('show')) {
        node.addEventListener('shown.bs.modal', () => modal.hide(), { once: true });
        return;
    }
    modal.hide();
}

export function hideModal(modalId: string): void {
    const node = modalElement(modalId);
    if (node) hideModalElement(node);
}

/**
 * Hide a modal and run something once it is really gone.
 *
 * The reason this is not just `hideModal(); action();`: a list reload usually
 * swaps a container that CONTAINS the modal placeholder -- the console's detail
 * dialogs are rendered at the end of the list partial they belong to -- so
 * firing the swap while the dialog is still fading out detaches the element
 * mid-transition. `hidden.bs.modal` then fires on a node that is no longer in
 * the document, so nothing listening at document level (the "the tab wrote
 * something, reload the list" flags) ever hears it.
 *
 * Waiting costs one fade. The fallback timer is there because a modal that was
 * never shown, or a page with no Bootstrap, fires no event at all and the
 * caller's work must still happen.
 */
export function hideModalThen(modalId: string, action: () => void, fallbackMs = 500): void {
    const node = modalElement(modalId);
    if (!node) {
        action();
        return;
    }
    let done = false;
    const run = (): void => {
        if (done) return;
        done = true;
        action();
    };
    node.addEventListener('hidden.bs.modal', run, { once: true });
    setTimeout(run, fallbackMs);
    hideModalElement(node);
}

/**
 * Fill and show a modal container. With a title the standard dialog chrome
 * is rendered (or, when the container already has a header, only the three
 * regions are replaced); without one the body is the complete dialog markup.
 * formId attaches validation to the form that arrived; focusId gets focus
 * once the modal is shown.
 */
export function showModal(modalId: string, title: string | null | undefined, body: string, footer?: string | null, formId?: string | null, focusId?: string | null): void {
    const node = modalElement(modalId);
    if (!node) return;
    const footerHtml = footer ?? '';

    if (title) {
        const header = node.querySelector('.modal-header');
        if (header) {
            header.classList.remove('alert-danger');
            header.innerHTML = title;
            const bodyHost = node.querySelector('.modal-body');
            const footerHost = node.querySelector('.modal-footer');
            if (bodyHost) bodyHost.innerHTML = body;
            if (footerHost) footerHost.innerHTML = footerHtml;
        } else {
            node.innerHTML =
                '<div class="modal-dialog modal-information"><div class="modal-content">'
                + `<div class="modal-header"><h4 class="modal-title">${title}</h4>`
                + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>'
                + `<div class="modal-body">${body}</div><div class="modal-footer">${footerHtml}</div></div></div>`;
        }
    } else {
        node.innerHTML = body;
    }

    if (formId) attach(formId);
    applyUtcToInputs(node);
    node.dispatchEvent(new CustomEvent('mcp:html-swapped', { bubbles: true, detail: { inserted: Array.from(node.children), mode: 'replace' } }));
    if (focusId) {
        node.addEventListener('shown.bs.modal', () => document.getElementById(focusId)?.focus(), { once: true });
    }
    showModalElement(node);
}

/** Replace a modal's content with a red-headed error dialog and show it. */
export function showModalError(modalId: string, title?: string | null, message1?: string | null, message2?: string | null): void {
    const node = modalElement(modalId);
    if (!node) return;
    const dialog = el('div', 'modal-dialog');
    dialog.setAttribute('role', 'document');
    const content = el('div', 'modal-content');
    const header = el('div', 'modal-header alert-danger');
    header.appendChild(el('h4', 'modal-title', title ?? ''));
    const close = el('button', 'btn-close');
    close.type = 'button';
    close.setAttribute('data-bs-dismiss', 'modal');
    close.setAttribute('aria-label', 'Close');
    header.appendChild(close);
    const body = el('div', 'modal-body');
    const p = el('p', 'text-danger');
    p.appendChild(el('strong', undefined, message1 ?? ''));
    if (message2) {
        p.appendChild(document.createElement('br'));
        p.appendChild(document.createTextNode(message2));
    }
    body.appendChild(p);
    content.append(header, body);
    dialog.appendChild(content);
    node.replaceChildren(dialog);
    showModalElement(node);
}

export interface ConfirmOptions {
    title?: string;
    /** Plain text, escaped. */
    message?: string;
    /** Raw markup, for callers that need emphasis; wins over message. */
    html?: string;
    confirmText?: string;
    cancelText?: string;
    /** Confirm-button colour and header tint: primary | danger | warning | … */
    variant?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
}

const confirmId = 'McpConfirmModal';

function confirmElement(): HTMLElement {
    let node = document.getElementById(confirmId);
    if (!node) {
        node = el('div', 'modal fade');
        node.id = confirmId;
        node.tabIndex = -1;
        node.setAttribute('aria-hidden', 'true');
        node.innerHTML =
            '<div class="modal-dialog modal-dialog-centered"><div class="modal-content">'
            + '<div class="modal-header"><h5 class="modal-title">'
            + '<i class="fas mcp-confirm-icon d-none me-2" aria-hidden="true"></i><span class="mcp-confirm-titletext"></span></h5>'
            + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>'
            + '<div class="modal-body"></div>'
            + '<div class="modal-footer"><button type="button" class="btn btn-secondary mcp-confirm-cancel" data-bs-dismiss="modal"></button>'
            + '<button type="button" class="btn mcp-confirm-ok"></button></div></div></div>';
        document.body.appendChild(node);
    }
    return node;
}

let confirmOk: ((event: Event) => void) | null = null;
let confirmHidden: ((event: Event) => void) | null = null;

/**
 * In-app replacement for window.confirm(): one shared modal, callbacks fire
 * after it has fully hidden so follow-up UI never fights the transition.
 * Also resolves a promise with the verdict for callers that prefer await.
 */
export function confirm(options: ConfirmOptions = {}): Promise<boolean> {
    const node = confirmElement();
    const variant = options.variant ?? 'primary';

    const titleText = node.querySelector<HTMLElement>('.mcp-confirm-titletext');
    if (titleText) titleText.textContent = options.title ?? 'Confirm';

    const header = node.querySelector<HTMLElement>('.modal-header');
    header?.classList.remove('bg-danger', 'bg-warning', 'text-light', 'text-dark');
    const close = header?.querySelector<HTMLElement>('.btn-close');
    close?.classList.remove('btn-close-white');
    const icon = node.querySelector<Element>('.mcp-confirm-icon');
    if (icon) {
        // setAttribute, NOT .className: Font Awesome replaces the <i> with an
        // <svg>, and SVGElement.className is a read-only SVGAnimatedString, so
        // assigning to it throws a TypeError. The throw happened before the
        // dialog was shown, so EVERY confirm -- every delete in the console --
        // silently did nothing once Font Awesome had processed the page. Found
        // by the Phase 3.3 write round-trip, where two deletes reported success
        // and left the row in place.
        icon.setAttribute('class', 'fas mcp-confirm-icon d-none me-2');
        if (variant === 'danger') {
            header?.classList.add('bg-danger', 'text-light');
            close?.classList.add('btn-close-white');
            icon.classList.remove('d-none');
            icon.classList.add('fa-exclamation-triangle');
        } else if (variant === 'warning') {
            header?.classList.add('bg-warning', 'text-dark');
            icon.classList.remove('d-none');
            icon.classList.add('fa-exclamation-circle');
        }
    }

    const body = node.querySelector<HTMLElement>('.modal-body');
    if (body) {
        if (options.html) body.innerHTML = options.html;
        else body.textContent = options.message ?? '';
    }

    const cancel = node.querySelector<HTMLElement>('.mcp-confirm-cancel');
    if (cancel) cancel.textContent = options.cancelText ?? 'Cancel';
    const ok = node.querySelector<HTMLButtonElement>('.mcp-confirm-ok');
    if (ok) {
        ok.textContent = options.confirmText ?? 'OK';
        ok.className = `btn mcp-confirm-ok btn-${variant}`;
    }

    return new Promise<boolean>(resolve => {
        let confirmed = false;
        if (confirmOk && ok) ok.removeEventListener('click', confirmOk);
        if (confirmHidden) node.removeEventListener('hidden.bs.modal', confirmHidden);
        confirmOk = () => { confirmed = true; hideModalElement(node); };
        confirmHidden = () => {
            if (confirmed) options.onConfirm?.(); else options.onCancel?.();
            resolve(confirmed);
        };
        ok?.addEventListener('click', confirmOk);
        node.addEventListener('hidden.bs.modal', confirmHidden);
        node.addEventListener('shown.bs.modal', () => ok?.focus(), { once: true });
        showModalElement(node);
        if (!window.bootstrap) {
            // No Bootstrap (tests): there is no transition to wait for; treat the next OK click as the answer.
            ok?.addEventListener('click', () => confirmHidden?.(new Event('hidden.bs.modal')), { once: true });
        }
    });
}
